import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { EmailPersonalFolderTreeNode } from '../useData';

function flattenForParentPicker(
  nodes: EmailPersonalFolderTreeNode[],
  depth = 0
): { id: number; name: string; depth: number }[] {
  const out: { id: number; name: string; depth: number }[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, name: node.name, depth });
    if (node.children?.length) {
      out.push(...flattenForParentPicker(node.children, depth + 1));
    }
  }
  return out;
}

interface CreatePersonalFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When opening for "new subfolder", pass the parent id so it's pre-selected */
  initialParentId?: number | null;
  personalFolders: EmailPersonalFolderTreeNode[];
  onCreate: (body: { name: string; parentId?: number }) => Promise<void>;
}

const CreatePersonalFolderModal: React.FC<CreatePersonalFolderModalProps> = ({
  isOpen,
  onClose,
  initialParentId,
  personalFolders,
  onCreate,
}) => {
  const { colors } = useTheme();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number | null>(initialParentId ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setParentId(initialParentId ?? null);
      setError(null);
    }
  }, [isOpen, initialParentId]);

  const parentOptions = flattenForParentPicker(personalFolders);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a folder name');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onCreate({ name: trimmed, parentId: parentId ?? undefined });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalHeader>
        <ModalTitle>New folder</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1.5 rounded-md', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalContent>
          {error && (
            <p className={cn('text-sm mb-3 text-red-600 dark:text-red-400')} role="alert">
              {error}
            </p>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="create-folder-name" className={cn('block text-sm font-medium mb-1', colors.text)}>
                Folder name
              </label>
              <input
                id="create-folder-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Projects"
                maxLength={255}
                autoFocus
                className={cn(
                  'w-full px-3 py-2 rounded-md border text-sm',
                  colors.background,
                  colors.border,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500'
                )}
                aria-invalid={!!error}
              />
            </div>
            <div>
              <label htmlFor="create-folder-parent" className={cn('block text-sm font-medium mb-1', colors.text)}>
                Parent folder
              </label>
              <select
                id="create-folder-parent"
                value={parentId ?? ''}
                onChange={(e) => setParentId(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                className={cn(
                  'w-full px-3 py-2 rounded-md border text-sm',
                  colors.background,
                  colors.border,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500'
                )}
              >
                <option value="">Top level</option>
                {parentOptions.map((f) => (
                  <option key={f.id} value={f.id}>
                    {'\u00A0'.repeat(f.depth * 2)}{f.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CreatePersonalFolderModal;
